"use client";

import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import type { Ref } from "react";
import { observer } from "mobx-react";
import { reaction } from "mobx";

import classNames from "classnames";
import styles from "./styles/_BlockCS.module.scss";

import dynamic from "next/dynamic";
import ErrorBlock from "../SchoolBlocks/ErrorBlock";
import ApiClient from "../../../backends/ApiClient";
import { useStores } from "../../../stores/useStores";
import { useTranslateClassName } from "../../../hooks/useTranslateClassName";
import { terraceTypeInfo } from "./terraceTypeInfo";

export const deleteContentItem = async (contentItem) => {
  const client = await ApiClient();
  await client.DELETE("/organizations/{organization_pk}/content-items/{id}/", {
    params: {
      path: {
        id: contentItem.id,
        organization_pk: contentItem.organization_id,
      },
    },
  });
};

export const enum ITerraceTypes {
  EVENTS = "events",
  NEWS = "news",
  MEDIA = "media",
  ABOUT = "about",
  MAP = "map",
  HTML = "html",
  FILES = "files",
  HERO = "hero",
  LINKS = "links",
  STAFF = "staff",
  STATEMENT = "statement",
  SHORTCUTS = "shortcuts",
  SHOP = "shop",
  ACCORDION = "accordion",
  SPOTLIGHT = "spotlight",
}

const terraceTypesWithContentItems = [
  ITerraceTypes.EVENTS,
  ITerraceTypes.NEWS,
  ITerraceTypes.MEDIA,
  ITerraceTypes.FILES,
];

const blockTypes = {
  [ITerraceTypes.MAP]: dynamic(() => import("./MapBlock/MapBlock")),
  [ITerraceTypes.EVENTS]: dynamic(() => import("./EventsBlock/EventsBlock")),
  [ITerraceTypes.ABOUT]: dynamic(() => import("./AboutBlock/AboutBlock")),
  [ITerraceTypes.HTML]: dynamic(() => import("./HTMLBlock/HTMLBlock")),
  [ITerraceTypes.NEWS]: dynamic(() => import("./NewsBlock/NewsBlock")),
  [ITerraceTypes.MEDIA]: dynamic(() => import("./GalleryBlock/GalleryBlock")),
  [ITerraceTypes.FILES]: dynamic(
    () => import("./FilesFoldersBlock/FilesFoldersBlock"),
  ),
  [ITerraceTypes.LINKS]: dynamic(() => import("./LinksTerrace/LinksTerrace")),
  [ITerraceTypes.STAFF]: dynamic(() => import("./StaffBlock/StaffBlock")),
  [ITerraceTypes.STATEMENT]: dynamic(
    () => import("./StatementBlock/StatementBlock"),
  ),
  [ITerraceTypes.SHORTCUTS]: dynamic(
    () => import("./ShortcutsTerrace/ShortcutsTerrace"),
  ),
  [ITerraceTypes.SHOP]: dynamic(() => import("./ShopTerrace/ShopTerrace")),
  [ITerraceTypes.ACCORDION]: dynamic(
    () => import("./AccordionTerrace/AccordionTerrace"),
  ),
  [ITerraceTypes.SPOTLIGHT]: dynamic(
    () => import("./SpotlightTerrace/SpotlightTerrace"),
  ),
};

export type ITerraceProps = {
  aboveTheFold: boolean;
  handleUpdateContentItem: (
    contentItem: ContentItem,
    organization: BaseOrganization,
  ) => void;
  handleDeleteContentItem: (contentItem: ContentItem) => void;
  initialBlockPromise: null | Promise<any>;
};

const Block = observer(
  forwardRef(function Block(
    props: {
      blockObj: ITerrace;
      initialBlockPromise: null | Promise<any>;
      aboveTheFold: boolean;
    },
    ref: Ref<HTMLElement>,
  ) {
    const { interfaceStore, gridStore, modalStore } = useStores();

    const [aboveTheFold, setAboveTheFold] = useState(props.aboveTheFold);
    const blockRef = useRef<HTMLElement | null>(null);
    const translateClassName = useTranslateClassName();

    const BlockTypeComponent = blockTypes[props.blockObj.type] || ErrorBlock;

    // Point both the parent <CSSTransition>'s nodeRef (which drives the exit
    // collapse/fade in TerracedGrid) and our local blockRef (used by the scroll
    // reaction below) at the same <article>.
    const setBlockRef = useCallback(
      (el: HTMLElement | null) => {
        blockRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) ref.current = el;
      },
      [ref],
    );

    useEffect(() => {
      const r = reaction(
        () => interfaceStore.scrollPosition,
        () => {
          if (blockRef?.current && !aboveTheFold) {
            const rect = blockRef.current?.getBoundingClientRect();
            if (
              rect &&
              rect.top < window.innerHeight - window.innerHeight / 4 &&
              !aboveTheFold
            ) {
              setAboveTheFold(true);
            }
          }
        },
        { fireImmediately: true },
      );
      return () => r();
    }, []);

    async function handleDeleteContentItem(contentItem) {
      await deleteContentItem(contentItem);
      modalStore.clearAllModals();
      gridStore.setBlocks([
        {
          ...props.blockObj,
          content_items: props.blockObj.content_items?.filter(
            (c) => c.id !== contentItem.id,
          ),
        },
      ]);
    }

    const blockClassName = classNames({
      [styles.block]: true,
      [styles.blockAboveTheFold]: aboveTheFold,
      [translateClassName]: !terraceTypesWithContentItems.includes(
        props.blockObj.type,
      ),
    });

    return (
      <article
        className={blockClassName}
        data-id={props.blockObj.id}
        data-block-type={props.blockObj.type}
        ref={setBlockRef}
        id={props.blockObj.htmlId}
      >
        <BlockTypeComponent
          blockObj={props.blockObj}
          aboveTheFold={aboveTheFold}
          handleDeleteContentItem={handleDeleteContentItem}
          initialBlockPromise={props.initialBlockPromise}
        />
      </article>
    );
  }),
);

export const terraceTypeToTitleMap: { [value: string]: string } =
  Object.fromEntries(
    Object.entries(terraceTypeInfo).map(([type, info]) => [type, info.title]),
  );

export default Block;
